import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as userAccessRequestActions } from '../../../actions/userAccessRequestActions';
import PropTypes from 'prop-types'
import * as types from '../../../constants/actionTypes';
import Drawer from '../../Common/Drawer/Drawer';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../Common/Notification';
import AccessCodeForm from './AccessCodeForm';
import UserProfileForm from './UserProfileForm';
import EmailNotificationsForm from './EmailNotificationsForm';
import MomentTz from 'moment-timezone';
import { getRandomHourOfDay, getRandomDayOfWeek } from '../../../helpers/dateTime';

const Steps = {
    AccessCode: 'ACCESS_CODE',
    UserProfile: 'USER_PROFILE',
    EmailNotification: 'EMAIL_NOTIFICATION'
};

export class AddSubscriptionDrawer extends Component {

    constructor(props, context) {
        super(props, context);

        const userRequest = {
            accessCode: '',
            subscriptionId: 0,
            isSubscriptionSSOEnabled: false,
            employeeId: '',
            managementCompanyId: '',
            communityIds: '',
            departmentIds: '',
            sendWeeklyEmail: false,
            sendDailyEmail: false,
            dayOfWeekId: getRandomDayOfWeek().toString(),
            hourOfDayId: getRandomHourOfDay().toString(),
            locationId: '',
            additionalNotes: '',
            timezoneOffset: 0
        }
        
        this.state = {
            isLoading: false,
            isAccessCodeFormValid: true,
            isUserProfileFormValid: true,
            isEmailNotificationFormValid: true,
            hasEmailBeenModified: false,
            currentStep: Steps.AccessCode,
            blankUserRequest: userRequest,
            userRequest: userRequest,
            communities: [],
            countries: [],
            departments: [],
            managementCompanies: [],
            allLocations: [],
            availableLocations: [],
            hoursOfTheDay: [],
            daysOfTheWeek: []
        };
    }

    loadFilters = async () => {
        try {
            this.setState({ isLoading: true });
            var response = await this.props.userAccessRequestActions.loadUserAccessRequestProfileFilters(this.state.userRequest.subscriptionId);
            if (response.type === types.LOAD_USER_REQUEST_PROFILE_FILTERS_SUCCESS) {
                this.setState({
                    communities: response.payload.communities,
                    countries: response.payload.countries,
                    departments: response.payload.departments,
                    managementCompanies: response.payload.managementCompanies,
                    allLocations: response.payload.locations,
                    availableLocations: [], //initially we dont want to expose any locations until the user has selected a mgmt comp
                    hoursOfTheDay: response.payload.hoursOfDay,
                    daysOfTheWeek: response.payload.daysOfWeek
                });
            } else if (response.type === types.LOAD_USER_REQUEST_PROFILE_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    validateAccessCodeForm = () => {
        return this.state.userRequest.accessCode && this.state.userRequest.accessCode.trim().length > 0;
    }

    onAccessCodeStepContinue = async () => {
        try {
            if (!this.validateAccessCodeForm()) {
                this.setState({ isAccessCodeFormValid: false });
                return;
            }

            this.setState({ isLoading: true });
            const response = await this.props.userAccessRequestActions.checkAddSubscriptionAccessCode(this.state.userRequest.accessCode);
            if (response.type === types.CHECK_ADD_SUB_ACCESS_CODE_SUCCESS) {
                this.setState({
                    userRequest: {
                        ...this.state.userRequest,
                        subscriptionId: response.payload.subscriptionId,
                        isSubscriptionSSOEnabled: response.payload.isSSOEnabled
                    },
                    currentStep: Steps.UserProfile
                });
                await this.loadFilters();
            } else if (response.type === types.CHECK_ADD_SUB_ACCESS_CODE_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    onFormChange = (name, value) => {
        this.setState({
            userRequest: {
                ...this.state.userRequest,
                [name]: value
            }
        });
    }

    onManagementCompanyChange = (name, value) => {
        const locations = this.state.allLocations.filter(x => x.managementCompanyId.toString() === value);
        this.setState({
            availableLocations: locations,
            userRequest: {
                ...this.state.userRequest,
                managementCompanyId: value,
            }
        })
    }

    validateUserProfileForm = () => {
        const {
            employeeId,
            managementCompanyId,
            communityIds,
            sendWeeklyEmail,
            dayOfWeekId,
            sendDailyEmail,
            hourOfDayId,
            locationId,
            isSubscriptionSSOEnabled
        } = this.state.userRequest;

        return (
            managementCompanyId && managementCompanyId.trim().length > 0
            && communityIds && communityIds.trim().length > 0
            && (!sendWeeklyEmail || (sendWeeklyEmail && dayOfWeekId && dayOfWeekId.trim().length > 0))
            && (!sendDailyEmail || (sendDailyEmail && hourOfDayId && hourOfDayId.trim().length > 0))
            && locationId && locationId.trim().length > 0
            && (!isSubscriptionSSOEnabled || (isSubscriptionSSOEnabled && employeeId && employeeId.trim().length > 0))
        );
    }

    onUserProfileStepContinue = () => {

        if (!this.validateUserProfileForm()) {
            this.setState({ isUserProfileFormValid: false });
        } else {
            this.setState({ currentStep: Steps.EmailNotification });
        }
    }

    validateEmailNotificationStepForm = () => {
        const {
            sendWeeklyEmail,
            dayOfWeekId,
            sendDailyEmail,
            hourOfDayId,
        } = this.state.userRequest;

        return (
            (!sendWeeklyEmail || (sendWeeklyEmail && dayOfWeekId && dayOfWeekId.trim().length > 0))
            && (!sendDailyEmail || (sendDailyEmail && hourOfDayId && hourOfDayId.trim().length > 0))
        );
    }

    getTimezoneOffset = () => {
        const tz = MomentTz.tz.guess();
        return MomentTz.tz(tz)._offset;
    }

    onEmailNotificationStepContinue = async () => {
        try {
            if (!this.validateEmailNotificationStepForm()) {
                this.setState({ isEmailNotificationFormValid: false });
                return;
            }

            this.setState({ isLoading: true });
            const request = { 
                ...this.state.userRequest,
            };

            if (request.communityIds && request.communityIds.trim().length > 0) {
                request.communityIds = request.communityIds.split(',');
            }

            if (request.departmentIds && request.departmentIds.trim().length > 0) {
                request.departmentIds = request.departmentIds.split(',');
            }

            if (!this.state.userRequest.sendWeeklyEmail) {
                request.dayOfWeekId = null;
            }

            if (!this.state.userRequest.sendDailyEmail) {
                request.hourOfDayId = null;
            }

            request.timezoneOffset = this.getTimezoneOffset();

            const response = await this.props.userAccessRequestActions.createAddSubscriptionRequest(request);
            if (response.type === types.CREATE_ADD_SUBSCRIPTION_REQUEST_SUCCESS) {
                Notification("success", "Success", "User Access Request submitted successfully");
                this.props.onComplete();
            } else if (response.type === types.CREATE_ADD_SUBSCRIPTION_REQUEST_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    onPrimaryClick = () => {
        switch (this.state.currentStep){
            case Steps.AccessCode: 
                this.onAccessCodeStepContinue();
                break;
            case Steps.UserProfile:
                this.onUserProfileStepContinue();
                break;
            case Steps.EmailNotification:
                this.onEmailNotificationStepContinue();
        }
    }

    renderStep = () => {
        switch (this.state.currentStep) {
            case Steps.AccessCode: {
                return <AccessCodeForm
                    userRequest={this.state.userRequest}
                    onContinue={this.onPrimaryClick}
                    onCancel={this.props.onCancel}
                    onFormChange={this.onFormChange}
                    isFormValid={this.state.isAccessCodeFormValid}
                />
            }
            case Steps.UserProfile: {
                return <UserProfileForm
                    userRequest={this.state.userRequest}
                    onContinue={this.onPrimaryClick}
                    onCancel={this.props.onCancel}
                    onFormChange={this.onFormChange}
                    isFormValid={this.state.isUserProfileFormValid}
                    userAccessRequestActions={this.props.userAccessRequestActions}
                    onManagementCompanyChange={this.onManagementCompanyChange}
                    communities={this.state.communities}
                    countries={this.state.countries}
                    departments={this.state.departments}
                    managementCompanies={this.state.managementCompanies}
                    locations={this.state.availableLocations}
                />
            }
            case Steps.EmailNotification: {
                return <EmailNotificationsForm
                    userRequest={this.state.userRequest}
                    onContinue={this.onPrimaryClick}
                    onCancel={this.props.onCancel}
                    onFormChange={this.onFormChange}
                    isFormValid={this.state.isEmailNotificationFormValid}
                    hoursOfTheDay={this.state.hoursOfTheDay}
                    daysOfTheWeek={this.state.daysOfTheWeek}
                />
            }
            default: {
                Notification("error", "Error", "Unknown User Access Request Step");
                return null;
            }
        }
    }

    render() {

        const saveText = (this.state.currentStep !== Steps.EmailNotification) ? "Continue" : "Submit Request";

        return (
            <Drawer isWide={false} title='Add Subscription'
                onSaveClick={this.onPrimaryClick}
                saveText={saveText}
                onCancelClick={this.props.onCancel}>
                <div className="user-access-request-wizard">
                    <LoadingIndicator isLoading={this.state.isLoading}>
                        {this.renderStep()}
                    </LoadingIndicator>
                </div>
            </Drawer>            
        )
    }
}

AddSubscriptionDrawer.propTypes = {
    user: PropTypes.object.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    userAccessRequestActions: PropTypes.object.isRequired
};

function mapDispatchToProps(dispatch) {
    return {
        userAccessRequestActions: bindActionCreators(userAccessRequestActions, dispatch)
    };
}

function mapStateToProps(state){
    return {
        windowWidth: state.window.width
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddSubscriptionDrawer);